'use client';

import { useState, useEffect } from 'react';

interface CalculatorFormProps {
  language: 'en' | 'es';
}

interface Question {
  id: string;
  question: string;
  options: { value: string; label: string; score: number; multiplier?: number }[];
  weight: number;
}

const CalculatorForm = ({ language }: CalculatorFormProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [result, setResult] = useState<{
    score: number;
    costRange: string;
    category: 'green' | 'yellow' | 'red';
    message: string;
  } | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('calculator-form');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const questions: { en: Question[]; es: Question[] } = {
    en: [
      {
        id: 'q1',
        question: 'Was your current legal structure (LLC, Corp, none) strategically chosen for your tax situation?',
        options: [
          { value: 'professional', label: 'Yes, with professional advice', score: 10 },
          { value: 'research', label: 'I chose it myself with some research', score: 5 },
          { value: 'easy', label: 'I used the easiest/cheapest or have no structure', score: 1 },
        ],
        weight: 25,
      },
      {
        id: 'q2',
        question: 'Are you leveraging multi-jurisdiction structures to legally optimize taxes?',
        options: [
          { value: 'optimized', label: 'Yes, I have an optimized international structure', score: 10 },
          { value: 'considered', label: "I've considered it but haven't implemented", score: 5 },
          { value: 'single', label: 'No, everything is in one jurisdiction', score: 1 },
        ],
        weight: 20,
      },
      {
        id: 'q3',
        question: "What's your approximate annual revenue (personal + business)?",
        options: [
          { value: 'under100k', label: 'Under $100K', score: 10, multiplier: 1 },
          { value: '100k-500k', label: '$100K - $500K', score: 5, multiplier: 2 },
          { value: 'over500k', label: 'Over $500K', score: 1, multiplier: 4 },
        ],
        weight: 20,
      },
      {
        id: 'q4',
        question: 'Are your personal assets separated and protected from your business liabilities?',
        options: [
          { value: 'separated', label: 'Yes, completely separated with proper structure', score: 10 },
          { value: 'partial', label: "Partially — I have an LLC but not sure if it's enough", score: 5 },
          { value: 'no', label: "No, or I'm not sure", score: 1 },
        ],
        weight: 20,
      },
      {
        id: 'q5',
        question: 'When was the last time a professional reviewed your structure?',
        options: [
          { value: 'recent', label: 'Within the last year', score: 10 },
          { value: '1-3years', label: '1-3 years ago', score: 5 },
          { value: 'never', label: 'Never or over 3 years ago', score: 1 },
        ],
        weight: 15,
      },
    ],
    es: [
      {
        id: 'q1',
        question: '¿Tu estructura legal actual (LLC, Corp, nada) fue elegida estratégicamente para tu situación fiscal?',
        options: [
          { value: 'professional', label: 'Sí, con asesoría profesional', score: 10 },
          { value: 'research', label: 'Lo elegí yo mismo con algo de investigación', score: 5 },
          { value: 'easy', label: 'Usé lo más fácil/barato o no tengo estructura', score: 1 },
        ],
        weight: 25,
      },
      {
        id: 'q2',
        question: '¿Estás aprovechando estructuras en múltiples jurisdicciones para optimizar impuestos legalmente?',
        options: [
          { value: 'optimized', label: 'Sí, tengo una estructura internacional optimizada', score: 10 },
          { value: 'considered', label: 'Lo he considerado pero no lo he implementado', score: 5 },
          { value: 'single', label: 'No, todo está en una sola jurisdicción', score: 1 },
        ],
        weight: 20,
      },
      {
        id: 'q3',
        question: '¿Cuál es tu ingreso anual aproximado (personal + negocio)?',
        options: [
          { value: 'under100k', label: 'Menos de $100K', score: 10, multiplier: 1 },
          { value: '100k-500k', label: '$100K - $500K', score: 5, multiplier: 2 },
          { value: 'over500k', label: 'Más de $500K', score: 1, multiplier: 4 },
        ],
        weight: 20,
      },
      {
        id: 'q4',
        question: '¿Tus activos personales están separados y protegidos de las responsabilidades de tu negocio?',
        options: [
          { value: 'separated', label: 'Sí, completamente separados con estructura adecuada', score: 10 },
          { value: 'partial', label: 'Parcialmente — tengo LLC pero no estoy seguro si es suficiente', score: 5 },
          { value: 'no', label: 'No, o no estoy seguro', score: 1 },
        ],
        weight: 20,
      },
      {
        id: 'q5',
        question: '¿Cuándo fue la última vez que un profesional revisó tu estructura?',
        options: [
          { value: 'recent', label: 'En el último año', score: 10 },
          { value: '1-3years', label: 'Hace 1-3 años', score: 5 },
          { value: 'never', label: 'Nunca o hace más de 3 años', score: 1 },
        ],
        weight:15,
      },
    ],
  };

  const content = {
    en: {
      emailLabel: 'Email address',
      emailPlaceholder: 'your@email.com',
      calculate: 'Calculate My Cost',
      next: 'Next',
      previous: 'Previous',
      checkEmail: 'Check your email for the full analysis.',
      bookCall: 'Book a Call',
      categories: {
        green: 'Your structure is optimized. Minor details to review.',
        yellow: "You\'re leaving money on the table. We can fix that.",
        red: 'Your structure is costing you. Let\'s talk.',
      },
    },
    es: {
      emailLabel: 'Dirección de correo',
      emailPlaceholder: 'tu@correo.com',
      calculate: 'Calcular Mi Costo',
      next: 'Siguiente',
      previous: 'Anterior',
      checkEmail: 'Revisa tu correo para el análisis completo.',
      bookCall: 'Agendar Llamada',
      categories: {
        green: 'Tu estructura está optimizada. Hay detalles menores que revisar.',
        yellow: 'Estás dejando dinero en la mesa. Podemos arreglarlo.',
        red: 'Tu estructura te está costando caro. Hablemos.',
      },
    },
  };

  const calculateScore = () => {
    let totalScore = 0;
    let multiplier = 1;

    questions[language].forEach((question) => {
      const answer = answers[question.id];
      const option = question.options.find((opt) => opt.value === answer);
      if (option) {
        totalScore += (option.score * question.weight) / 100;
        if (option.multiplier) {
          multiplier = option.multiplier;
        }
      }
    });

    const score = totalScore * 10;

    let costRange = '';
    let category: 'green' | 'yellow' | 'red' = 'green';

    if (score >= 8) {
      costRange = `$${0 * multiplier} - $${2000 * multiplier}`;
      category = 'green';
    } else if (score >= 5) {
      costRange = `$${2000 * multiplier} - $${10000 * multiplier}`;
      category = 'yellow';
    } else {
      costRange = `$${10000 * multiplier} - $${50000 * multiplier}+`;
      category = 'red';
    }

    setResult({
      score: parseFloat(score.toFixed(1)),
      costRange,
      category,
      message: content[language].categories[category],
    });
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setStep(1);
    }
  };

  const handleAnswerSelect = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (step < questions[language].length) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleCalculate = () => {
    calculateScore();
    setStep(questions[language].length + 1);
  };

  const currentQuestion = questions[language][step - 1];
  const progress = (step / (questions[language].length + 1)) * 100;

  return (
    <section id="calculator-form" className="py-20 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        {step > 0 && step <= questions[language].length && (
          <div className="mb-8">
            <div className="h-1 bg-muted sharp overflow-hidden">
              <div
                className="h-full bg-primary sharp transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Email Step */}
        {step === 0 && (
          <form
            onSubmit={handleEmailSubmit}
            className={`space-y-6 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
          >
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                {content[language].emailLabel}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={content[language].emailPlaceholder}
                required
                className="w-full px-4 py-3 bg-input border hairline border-border sharp text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <button
              type="submit"
              className="w-full px-8 py-4 text-base font-semibold text-primary-foreground bg-primary sharp hover:bg-primary/90 transition-all duration-300"
            >
              {content[language].next}
            </button>
          </form>
        )}

        {/* Question Steps */}
        {step > 0 && step <= questions[language].length && (
          <div className={`space-y-6 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">
                Question {step} of {questions[language].length}
              </p>
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {currentQuestion.question}
              </h2>
              <div className="space-y-3">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswerSelect(currentQuestion.id, option.value)}
                    className={`w-full px-6 py-4 text-left border hairline sharp transition-all duration-300 ${
                      answers[currentQuestion.id] === option.value
                        ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/50'
                    }`}
                  >
                    <span className="text-base text-foreground">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-4">
              {step > 1 && (
                <button
                  onClick={handlePrevious}
                  className="px-8 py-4 text-base font-semibold text-foreground bg-muted sharp hover:bg-muted/80 transition-all duration-300"
                >
                  {content[language].previous}
                </button>
              )}
              {step < questions[language].length ? (
                <button
                  onClick={handleNext}
                  disabled={!answers[currentQuestion.id]}
                  className="flex-1 px-8 py-4 text-base font-semibold text-primary-foreground bg-primary sharp hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {content[language].next}
                </button>
              ) : (
                <button
                  onClick={handleCalculate}
                  disabled={!answers[currentQuestion.id]}
                  className="flex-1 px-8 py-4 text-base font-semibold text-primary-foreground bg-primary sharp hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {content[language].calculate}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {result && step > questions[language].length && (
          <div className={`space-y-8 ${isVisible ? 'animate-fadeInScale' : 'opacity-0'}`}>
            <div className="text-center space-y-4">
              <div
                className={`inline-block px-4 py-2 sharp text-sm font-bold uppercase tracking-wider ${
                  result.category === 'green' ?'bg-success text-success-foreground'
                    : result.category === 'yellow' ?'bg-warning text-warning-foreground' :'bg-error text-error-foreground'
                }`}
              >
                {result.category === 'green' ? 'Optimized' : result.category === 'yellow' ? 'Missed Opportunities' : 'Significant Losses'}
              </div>
              <div className="font-mono text-7xl font-bold text-foreground">
                {result.score}/10
              </div>
              <div className="font-mono text-3xl font-bold text-muted-foreground">
                {result.costRange}/year
              </div>
              <p className="text-lg text-foreground max-w-lg mx-auto">
                {result.message}
              </p>
            </div>

            <div className="p-6 bg-muted sharp text-center">
              <p className="text-sm text-muted-foreground mb-4">
                {content[language].checkEmail}
              </p>
            </div>

            <div className="text-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary-foreground bg-primary sharp hover:bg-primary/90 transition-all duration-300"
              >
                {content[language].bookCall}
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CalculatorForm;